import { useCallback, useEffect, useRef, useState } from "react";

type OnFinal = (text: string) => void;

export function useSpeechRecognition(onFinal?: OnFinal) {
    const SR: typeof window.SpeechRecognition | typeof window.webkitSpeechRecognition | undefined =
        (typeof window !== "undefined" && (window as any).SpeechRecognition) ||
        (typeof window !== "undefined" && (window as any).webkitSpeechRecognition);

    const supported = Boolean(SR);
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");

    useEffect(() => {
        if (!supported) return;
        const rec: SpeechRecognition = new (SR as any)();
        rec.lang = "ru-RU";
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        rec.continuous = true;

        rec.onresult = (event: SpeechRecognitionEvent) => {
            let agg = "";
            let isFinal = false;
            for (let i = event.resultIndex; i < event.results.length; i++) {
                agg += event.results[i][0].transcript;
                if (event.results[i].isFinal) isFinal = true;
            }
            setTranscript(agg);
            if (isFinal && onFinal) onFinal(agg.trim());
        };

        rec.onerror = () => setIsListening(false);
        rec.onend = () => setIsListening(false);

        recognitionRef.current = rec;
        return () => {
            rec.onresult = null as any;
            rec.onerror = null as any;
            rec.onend = null as any;
            try { rec.stop(); } catch {}
            recognitionRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supported]);

    const start = useCallback(() => {
        if (!supported || !recognitionRef.current) return;
        try {
            recognitionRef.current.start();
            setIsListening(true);
        } catch {}
    }, [supported]);

    const stop = useCallback(() => {
        if (!supported || !recognitionRef.current) return;
        try {
            recognitionRef.current.stop();
            setIsListening(false);
        } catch {}
    }, [supported]);

    return { supported, isListening, transcript, start, stop, setTranscript };
}
